/** React wrapper for Handsontable v16 */
import { forwardRef, useEffect, useRef, useImperativeHandle, type ForwardedRef } from 'react';
import Handsontable from 'handsontable-v16';
import type { GridSettings } from '@/support/hot-v16.types';
import { _notNil } from '@/littledash';
import 'handsontable-v16/dist/handsontable.full.css';
import './SpreadsheetV16.scss';

export interface HotTableRef {
  hotInstance: Handsontable | null;
}

export type HotTableProps = GridSettings & {
  licenseKey?: string;
  testId?: string;
};

export const HotTable = forwardRef<HotTableRef, HotTableProps>((props, ref: ForwardedRef<HotTableRef>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hotInstanceRef = useRef<Handsontable | null>(null);

  useImperativeHandle(ref, () => ({
    get hotInstance() {
      return hotInstanceRef.current;
    },
  }));

  // Initialize Handsontable
  useEffect(() => {
    if (_notNil(containerRef.current)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { licenseKey, testId, ...settings } = props;

      hotInstanceRef.current = new Handsontable(containerRef.current, {
        ...settings,
        licenseKey: licenseKey ?? '',
        renderAllColumns: true,
      });
    }

    return () => {
      if (_notNil(hotInstanceRef.current)) {
        hotInstanceRef.current.destroy();
        hotInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (_notNil(hotInstanceRef.current)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { licenseKey, testId, ...settings } = props;
      hotInstanceRef.current.updateSettings(settings);
    }
  }, [props]);

  return <div ref={containerRef} data-test-component="HotTable" data-testid={props.testId} />;
});

HotTable.displayName = 'HotTable';
